import { jsx as _jsx } from "react/jsx-runtime";
import isNull from "lodash/isNull";
import isUndefined from "lodash/isUndefined";
import { forwardRef, memo, useCallback, useEffect, useImperativeHandle, useRef } from "react";
import { useFindCurrentSegmentIndex } from "~/components/AssetModalV2/Content/Panel/Transcript/hooks/useFindCurrentSegmentIndex";
import { isInRange } from "~/components/AssetModalV2/Content/Panel/Transcript/utils";
import { useTranscriptScrollInfoContext } from "~/providers/TranscriptScrollInfoProvider";
import { useVideoTimestampObserverContext } from "~/providers/VideoTimestampObserverProvider";
const WORD_TOP_OFFSET = 24; // distance from the top of the list to make the current word visible
const WORD_HEIGHT = 24;
/**
 * This component observes current time of the video and current scroll position of the list
 * It is responsible for scrolling to the current word when transcript list scrolls
 */ const _CurrentLineObserver = /*#__PURE__*/ forwardRef((param, forwardedRef)=>{
    let { segments, listRef, onScrolledProgrammatically } = param;
    const elementRef = useRef(null);
    const { subscribe, getCurrentTime } = useVideoTimestampObserverContext();
    const currentSegmentIndex = useRef(-1);
    const currentLineIndex = useRef(-1);
    const topPosRef = useRef(0);
    const onScrolledProgrammaticallyRef = useRef(onScrolledProgrammatically);
    onScrolledProgrammaticallyRef.current = onScrolledProgrammatically;
    const isOnScreen = useRef(true);
    const { setCurrentWordPositionInfo, setScrollEnabled, getIsScrollEnabled, registerReturnToCurrentWord } = useTranscriptScrollInfoContext();
    const scrollToCurrentPosition = useCallback((isSmooth)=>{
        var _listRef_current;
        var _listRef_current_getScrollMargin;
        const scrollMargin = (_listRef_current_getScrollMargin = (_listRef_current = listRef.current) === null || _listRef_current === void 0 ? void 0 : _listRef_current.getScrollMargin()) !== null && _listRef_current_getScrollMargin !== void 0 ? _listRef_current_getScrollMargin : 0;
        if (!isUndefined(topPosRef.current)) {
            var _listRef_current1;
            onScrolledProgrammaticallyRef.current();
            const scrollPosition = topPosRef.current + scrollMargin - WORD_TOP_OFFSET;
            const scrollElement = (_listRef_current1 = listRef.current) === null || _listRef_current1 === void 0 ? void 0 : _listRef_current1.getScrollElement();
            isSmooth ? scrollElement === null || scrollElement === void 0 ? void 0 : scrollElement.scrollTo({
                top: scrollPosition,
                behavior: "smooth"
            }) : scrollElement === null || scrollElement === void 0 ? void 0 : scrollElement.scrollTo(0, scrollPosition);
        }
    }, [
        listRef
    ]);
    const returnToCurrentWord = useCallback(()=>scrollToCurrentPosition(true), [
        scrollToCurrentPosition
    ]);
    useImperativeHandle(forwardedRef, ()=>({
            returnToCurrentWord
        }));
    // Register the function with context so button can call it directly
    useEffect(()=>{
        const { unregister } = registerReturnToCurrentWord(returnToCurrentWord);
        return ()=>{
            unregister();
        };
    }, [
        registerReturnToCurrentWord,
        returnToCurrentWord
    ]);
    const findCurrentLineIndex = useCallback((timeInMs)=>{
        var _listRef_current;
        if (currentSegmentIndex.current === -1) return -1;
        const segmentHeightCache = (_listRef_current = listRef.current) === null || _listRef_current === void 0 ? void 0 : _listRef_current.getSegmentHeightCache(currentSegmentIndex.current);
        const segmentLines = segmentHeightCache === null || segmentHeightCache === void 0 ? void 0 : segmentHeightCache.linesPositions;
        if (!segmentLines) return -1;
        if (timeInMs < segmentLines[0].startTimeMs) {
            return 0;
        } else if (timeInMs > segmentLines[segmentLines.length - 1].endTimeMs) {
            return segmentLines.length - 1;
        }
        if (currentLineIndex.current !== -1 && segmentLines.length > currentLineIndex.current) {
            const segment = segmentLines[currentLineIndex.current];
            if (isInRange(timeInMs, [
                segment.startTimeMs,
                segment.endTimeMs
            ])) {
                return currentLineIndex.current;
            }
        }
        // user may set time on a video that is not bound to any line
        // in that case we want to pick the closest line after that time
        let minPastTime = Number.MAX_SAFE_INTEGER;
        let newLineIndex = -1;
        for(let index = 0; index < segmentLines.length; index++){
            const { startTimeMs, endTimeMs } = segmentLines[index];
            if (isInRange(timeInMs, [
                startTimeMs,
                endTimeMs
            ])) {
                newLineIndex = index;
                break;
            } else if (startTimeMs > timeInMs) {
                const pastTime = startTimeMs - timeInMs;
                if (pastTime < minPastTime) {
                    minPastTime = pastTime;
                    newLineIndex = index;
                }
            }
            if (endTimeMs > timeInMs) {
                break;
            }
        }
        return newLineIndex;
    }, [
        listRef
    ]);
    const setNewStylePosition = useCallback((param)=>{
        let { currentLineIndex, currentSegmentIndex } = param;
        if (!listRef.current) return;
        const segmentHeightCache = listRef.current.getSegmentHeightCache(currentSegmentIndex);
        const segmentTopPosition = listRef.current.getSegmentTopPosition(currentSegmentIndex);
        const segmentLines = segmentHeightCache === null || segmentHeightCache === void 0 ? void 0 : segmentHeightCache.linesPositions;
        if (!segmentLines) return;
        const segmentLine = segmentLines[currentLineIndex];
        const scrollMargin = listRef.current.getScrollMargin();
        if (isNull(segmentTopPosition) || !segmentHeightCache) return;
        topPosRef.current = segmentTopPosition + segmentHeightCache.segmentHeaderHeight + segmentLine.offset - scrollMargin;
        if (elementRef.current) {
            elementRef.current.style.top = "".concat(topPosRef.current, "px");
        }
        return topPosRef.current;
    }, [
        listRef
    ]);
    const scrollToPositionIfNotOnScreen = useCallback((newTopPosition)=>{
        var _listRef_current, _listRef_current1;
        const scroll = (_listRef_current = listRef.current) === null || _listRef_current === void 0 ? void 0 : _listRef_current.getScrollElement();
        if (!scroll) {
            return;
        }
        const scrollHeight = scroll.clientHeight;
        var _listRef_current_getScrollMargin;
        const scrollMargin = (_listRef_current_getScrollMargin = (_listRef_current1 = listRef.current) === null || _listRef_current1 === void 0 ? void 0 : _listRef_current1.getScrollMargin()) !== null && _listRef_current_getScrollMargin !== void 0 ? _listRef_current_getScrollMargin : 0;
        const topPosInScroll = newTopPosition + scrollMargin;
        const isAboveScreen = topPosInScroll < scroll.scrollTop;
        const isBelowScreen = topPosInScroll > scroll.scrollTop + scrollHeight;
        const isOutOfScreen = isAboveScreen || isBelowScreen;
        if (isOutOfScreen) {
            if (getIsScrollEnabled()) {
                scrollToCurrentPosition(false);
            } else if (isAboveScreen) {
                setCurrentWordPositionInfo("above-screen");
            } else {
                setCurrentWordPositionInfo("below-screen");
            }
        }
    }, [
        getIsScrollEnabled,
        listRef,
        scrollToCurrentPosition,
        setCurrentWordPositionInfo
    ]);
    const onCurrentSegmentChanged = useCallback((segmentIndex)=>{
        const timeInSeconds = getCurrentTime();
        if (isNull(timeInSeconds)) return;
        const timeInMs = timeInSeconds * 1000;
        currentSegmentIndex.current = segmentIndex;
        currentLineIndex.current = findCurrentLineIndex(timeInMs);
        const newPosition = setNewStylePosition({
            currentSegmentIndex: currentSegmentIndex.current,
            currentLineIndex: currentLineIndex.current
        });
        if (!isUndefined(newPosition)) {
            scrollToPositionIfNotOnScreen(newPosition);
        }
    }, [
        findCurrentLineIndex,
        getCurrentTime,
        scrollToPositionIfNotOnScreen,
        setNewStylePosition
    ]);
    // We have to find current segment index in order to scroll to correct word
    // Each word has a timestamp listener, so they can control scrolling themselves as the video plays
    // But if a user changes video time, it may happen tha words (transcript segment) is not mounted yet, because the list is virtualized
    // in that case we have to manually scroll to the segment, so that words can mount and start listening to video time
    useFindCurrentSegmentIndex({
        segments,
        callback: onCurrentSegmentChanged
    });
    useEffect(()=>{
        const subscription = subscribe((time)=>{
            if (isNull(time)) return;
            const timeInMs = time * 1000;
            currentLineIndex.current = findCurrentLineIndex(timeInMs);
            const newPosition = setNewStylePosition({
                currentSegmentIndex: currentSegmentIndex.current,
                currentLineIndex: currentLineIndex.current
            });
            // Also check if we need to scroll to the new position
            if (!isUndefined(newPosition)) {
                scrollToPositionIfNotOnScreen(newPosition);
            }
        });
        return ()=>{
            subscription.unsubscribe();
        };
    }, [
        findCurrentLineIndex,
        setNewStylePosition,
        subscribe,
        scrollToPositionIfNotOnScreen,
        getIsScrollEnabled
    ]);
    useEffect(()=>{
        var _listRef_current;
        const scroll = (_listRef_current = listRef.current) === null || _listRef_current === void 0 ? void 0 : _listRef_current.getScrollElement();
        if (!scroll || !elementRef.current) return;
        const wordOutsideOfScreenCallback = (entries)=>{
            const currentEntry = entries[0];
            if (!currentEntry) return;
            const { isIntersecting, rootBounds, boundingClientRect } = currentEntry;
            if (isIntersecting) {
                isOnScreen.current = true;
                setCurrentWordPositionInfo("on-screen");
                setScrollEnabled(true);
            } else {
                if (!rootBounds || !boundingClientRect) return;
                isOnScreen.current = false;
                const newWordPosition = boundingClientRect.top < rootBounds.top ? "above-screen" : "below-screen";
                setCurrentWordPositionInfo(newWordPosition);
            }
        };
        const wordOutsideOfScreenObserver = new IntersectionObserver(wordOutsideOfScreenCallback, {
            root: scroll,
            threshold: 1
        });
        wordOutsideOfScreenObserver.observe(elementRef.current);
        return ()=>{
            wordOutsideOfScreenObserver.disconnect();
        };
    }, [
        listRef,
        setCurrentWordPositionInfo,
        setScrollEnabled
    ]);
    useEffect(()=>{
        var _listRef_current;
        const scroll = (_listRef_current = listRef.current) === null || _listRef_current === void 0 ? void 0 : _listRef_current.getScrollElement();
        if (!scroll || !elementRef.current) return;
        const wordNearScreenEdgesCallback = (entries)=>{
            const currentEntry = entries[0];
            if (!currentEntry) return;
            const { isIntersecting } = currentEntry;
            if (!isIntersecting && getIsScrollEnabled()) {
                scrollToCurrentPosition(true);
            }
        };
        const wordNearScreenEdgesObserver = new IntersectionObserver(wordNearScreenEdgesCallback, {
            root: scroll,
            threshold: 0.05,
            rootMargin: "-120px"
        });
        wordNearScreenEdgesObserver.observe(elementRef.current);
        return ()=>{
            wordNearScreenEdgesObserver.disconnect();
        };
    }, [
        getIsScrollEnabled,
        listRef,
        scrollToCurrentPosition
    ]);
    const scrollEndTimeout = useRef();
    useEffect(()=>{
        var _listRef_current;
        const scroll = (_listRef_current = listRef.current) === null || _listRef_current === void 0 ? void 0 : _listRef_current.getScrollElement();
        if (!scroll) return;
        const scrollListener = ()=>{
            clearTimeout(scrollEndTimeout.current);
            scrollEndTimeout.current = window.setTimeout(()=>{
                if (!isOnScreen.current) {
                    if (scroll.scrollTop > topPosRef.current) {
                        setCurrentWordPositionInfo("above-screen");
                    } else {
                        setCurrentWordPositionInfo("below-screen");
                    }
                }
            }, 50); // Keep the same 50ms timeout as V1
        };
        scroll.addEventListener("scroll", scrollListener);
        return ()=>{
            scroll.removeEventListener("scroll", scrollListener);
        };
    }, [
        listRef,
        setCurrentWordPositionInfo
    ]);
    return /*#__PURE__*/ _jsx("div", {
        ref: elementRef,
        className: "pointer-events-none absolute w-full opacity-50",
        style: {
            height: WORD_HEIGHT,
            top: topPosRef.current,
            zIndex: 2
        }
    });
});
_CurrentLineObserver.displayName = "CurrentLineObserver";
export const CurrentLineObserver = /*#__PURE__*/ memo(_CurrentLineObserver);
CurrentLineObserver.displayName = "CurrentLineObserver";
